import React, { useState, useEffect, useMemo } from 'react';
import { LdSheet } from '../../components/ui/LdSheet';
import { LdFeld, LdAuswahl, LdFensterFuss } from '../../components/ld/LdFenster';
import './ausflug.css';
import { toast } from '../../lib/toast';
import { ausflugApi, AusflugTermin, AusflugTeilnehmer } from './ausflug-api';
import { ApiError } from '../../lib/api';
import '../schueler/U6Bearbeiten.css';

interface SchuelerOpt { id: number; vorname: string; nachname: string; klasse_id: number; }

export function TeilnehmerVerwaltenModal({ termin, vorhandeneIds, teilnehmer = [], onClose, onDone, onEntfernt }: {
  termin: AusflugTermin;
  vorhandeneIds: number[];          // schueler_id der bereits eingetragenen Teilnehmer
  teilnehmer?: AusflugTeilnehmer[]; // Welle U6a: für "Bereits dabei" mit Entfernen
  onClose: () => void;
  onDone: () => void;               // refresht das AusflugDetail
  onEntfernt?: () => void;          // Welle U6a: Liste im Detail neu laden, Modal bleibt offen
}) {
  // Welle U6a: Entfernen — 409 (Teilnehmer hat schon eine Fehlzeit) als dezenter Hinweis an der Zeile
  const [dabei, setDabei] = useState<AusflugTeilnehmer[]>(teilnehmer);
  const [entfernHinweis, setEntfernHinweis] = useState<Record<number, string>>({});
  async function entfernen(t: AusflugTeilnehmer) {
    try {
      await ausflugApi.entferneTeilnehmer(termin.id, t.schueler_id);
      setDabei(prev => prev.filter(x => x.schueler_id !== t.schueler_id));
      toast(`${t.vorname} ${t.nachname} entfernt`, 'success');
      onEntfernt?.();
    } catch (err: any) {
      if (err instanceof ApiError && err.status === 409) {
        setEntfernHinweis(h => ({ ...h, [t.schueler_id]: err.message }));
        // Hinweis kann unten in der scrollbaren Liste liegen → sichtbar machen
        requestAnimationFrame(() => document.getElementById(`ld-u6-tn-hinweis-${t.schueler_id}`)?.scrollIntoView({ block: 'nearest' }));
      }
      else toast(err.message ?? 'Entfernen fehlgeschlagen.', 'error');
    }
  }
  // Fall A: Klasse noch offen UND noch keine Teilnehmer → Klasse wählbar
  const klasseOffen = termin.klasse_id == null && vorhandeneIds.length === 0;

  const [laden, setLaden]     = useState(true);
  const [speichern, setSpeichern] = useState(false);
  const [klassen, setKlassen] = useState<{ klasse_id: number; klasse_name: string }[]>([]);
  const [alleSchueler, setAlleSchueler] = useState<SchuelerOpt[]>([]);
  const [klasseId, setKlasseId] = useState<number | ''>(termin.klasse_id ?? '');
  const [aktiv, setAktiv] = useState<Set<number>>(new Set());

  const vorhandenSet = useMemo(() => new Set(vorhandeneIds), [vorhandeneIds]);

  useEffect(() => {
    Promise.all([ausflugApi.klassen(), ausflugApi.schueler()])
      .then(([kl, sch]) => { setKlassen(kl); setAlleSchueler(sch); })
      .catch((err: any) => toast(err.message ?? 'Laden fehlgeschlagen.', 'error'))
      .finally(() => setLaden(false));
  }, []);

  // Kandidaten = aktive Schüler der gewählten Klasse, die noch NICHT dabei sind
  const kandidaten = useMemo(() => {
    if (klasseId === '') return [];
    return alleSchueler
      .filter(s => s.klasse_id === Number(klasseId) && !vorhandenSet.has(s.id))
      .sort((a, b) => a.nachname.localeCompare(b.nachname));
  }, [alleSchueler, klasseId, vorhandenSet]);

  // Bei Klasse-Auswahl: alle Kandidaten default aktiv
  useEffect(() => {
    setAktiv(new Set(kandidaten.map(s => s.id)));
  }, [kandidaten]);

  const klasseName = klassen.find(k => k.klasse_id === Number(klasseId))?.klasse_name
    ?? termin.klasse_name ?? null;

  async function hinzufuegen() {
    if (aktiv.size === 0) { toast('Bitte mindestens einen Schüler auswählen.', 'error'); return; }
    setSpeichern(true);
    try {
      const res = await ausflugApi.bulkAddTeilnehmer(
        termin.id, Array.from(aktiv), klasseOffen ? Number(klasseId) : undefined,
      );
      const teile = [`${res.hinzugefuegt_count} hinzugefügt`];
      if (res.bereits_vorhanden_count > 0) teile.push(`${res.bereits_vorhanden_count} bereits dabei`);
      toast(teile.join(', '), 'success');
      onDone();
    } catch (err: any) {
      toast(err.message ?? 'Hinzufügen fehlgeschlagen.', 'error');
    } finally {
      setSpeichern(false);
    }
  }

  const submitDisabled = speichern || aktiv.size === 0 || (klasseOffen && klasseId === '');

  return (
    <LdSheet open onClose={onClose} maxWidth={520}
      kopf={{ titel: 'Teilnehmer verwalten', kontext: `${termin.titel}${klasseName ? `, Klasse ${klasseName}` : ''}` }}
      fuss={<LdFensterFuss onAbbrechen={onClose} haupt={`Hinzufügen (${aktiv.size})`} onHaupt={hinzufuegen} laden={speichern} aus={submitDisabled} />}>
      {laden ? (
        <p className="ld-fe-text" role="status">Lädt …</p>
      ) : (
        <>
          {/* Klasse: wählbar (Fall A) oder fixiert (Fall B) */}
          {klasseOffen && (
            <LdFeld label="Klasse">
              <LdAuswahl aria-label="Klasse" value={klasseId === '' ? '' : String(klasseId)} placeholder="Klasse wählen" onChange={e => setKlasseId(e.target.value === '' ? '' : Number(e.target.value))}>
                {klassen.map(k => <option key={k.klasse_id} value={String(k.klasse_id)}>{k.klasse_name}</option>)}
              </LdAuswahl>
            </LdFeld>
          )}

          {/* Welle U6a: Bereits dabei — Entfernen */}
          {dabei.length > 0 && (
            <LdFeld label={`Bereits dabei (${dabei.length})`}>
              <div className="ld-af-wahl">
                {[...dabei].sort((a, b) => a.nachname.localeCompare(b.nachname)).map(t => (
                  <React.Fragment key={t.schueler_id}>
                    <div className="ld-u6-tn">
                      <span>{t.nachname}, {t.vorname}</span>
                      <button onClick={() => entfernen(t)} aria-label={`${t.vorname} ${t.nachname} entfernen`}>Entfernen</button>
                    </div>
                    {entfernHinweis[t.schueler_id] && <div id={`ld-u6-tn-hinweis-${t.schueler_id}`} className="ld-u6-tn__hinweis" role="status">{entfernHinweis[t.schueler_id]}</div>}
                  </React.Fragment>
                ))}
              </div>
            </LdFeld>
          )}

          {/* Teilnehmer-Liste */}
          <LdFeld label={`Hinzufügen${kandidaten.length > 0 ? ` (${aktiv.size} von ${kandidaten.length})` : ''}`}
            rechts={kandidaten.length > 0 ? (
              <span style={{ display: 'inline-flex', gap: 12 }}>
                <button type="button" className="ld-kl-link" onClick={() => setAktiv(new Set(kandidaten.map(s => s.id)))}>Alle</button>
                <button type="button" className="ld-kl-link" style={{ color: 'var(--ld-text-secondary)' }} onClick={() => setAktiv(new Set())}>Keine</button>
              </span>) : undefined}>
            {klasseOffen && klasseId === '' ? (
              <p className="ld-fe-text">Bitte zuerst eine Klasse wählen.</p>
            ) : kandidaten.length === 0 ? (
              <p className="ld-fe-text">{klasseOffen ? 'Keine Schüler in dieser Klasse.' : 'Alle Schüler dieser Klasse sind schon dabei.'}</p>
            ) : (
              <div className="ld-af-wahl">
                {kandidaten.map(s => {
                  const an = aktiv.has(s.id);
                  return (
                    <label key={s.id} className={an ? undefined : 'ist-aus'}>
                      <input type="checkbox" checked={an} onChange={() => setAktiv(prev => { const n = new Set(prev); if (n.has(s.id)) n.delete(s.id); else n.add(s.id); return n; })} />
                      <span>{s.nachname}, {s.vorname}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </LdFeld>
        </>
      )}
    </LdSheet>
  );
}
